import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import {
  Snackbar,
  Box,
  Typography,
  Paper,
  styled
} from '@mui/material';
import useSignalingStore from 'views/signalingStore';
import { useNavigate } from 'react-router-dom';
import { keyframes } from '@emotion/react';
import { playMessageSound, playNotificationSound } from '../utils/audioUtils';
import colors from 'colors/color';

import useUserId from 'hooks/useUserId';
export const NotificationContext = createContext();

export const useNotification = () => useContext(NotificationContext);
// Modern animation keyframes
const slideIn = keyframes`
  0% {
    transform: translateX(100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  0% {
    transform: translateX(0);
    opacity: 1;
  }
  100% {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const softPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.2);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(99, 102, 241, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);
  }
`;
const CompanyLogo = styled(Box)(({ theme, logo }) => ({
  minWidth: "59px",
  minHeight: "59px",
  maxWidth: "59px",
  maxHeight: "59px",
  background:
    "linear-gradient(135deg, rgba(0, 23, 231, 0.5) -54.69%, rgba(255, 204, 248, 0.5) 66.41%)",
  borderRadius: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: "0.75rem",
  textAlign: "center",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    minWidth: "45px",
    minHeight: "45px",
    maxWidth: "45px",
    maxHeight: "45px",
    borderRadius: 10,
  },
}));
// Glassmorphism blur effect
const glassEffect = {
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)', // For Safari support
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  borderRadius: '16px',
};

export const NotificationProvider = ({ children }) => {
  const [messageSnackbar, setMessageSnackbar] = useState({
    open: false,
    message: '',
    sender: '',
    senderId: null,
    senderImage: null
  });
  const [notificationSnackbar, setNotificationSnackbar] = useState({
    open: false,
    message: '',
    link: '',
    id: null,
    sender_name: 'System',
    sender_image: null
  });

  // Debug snackbar state changes
  useEffect(() => {
    console.log('🎯 Notification snackbar state changed:', {
      open: notificationSnackbar.open,
      message: notificationSnackbar.message?.substring(0, 30) + '...',
      sender: notificationSnackbar.sender_name,
      id: notificationSnackbar.id
    });
  }, [notificationSnackbar]);
  
  // Add state to track closed notifications
  const [closedNotifications, setClosedNotifications] = useState(() => {
    const saved = localStorage.getItem('closedNotifications');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Track if user has interacted with the page (for browser autoplay policy)
  const [userInteracted, setUserInteracted] = useState(false);
  const userInteractionTimeout = useRef(null);
  const user = useUserId();
  const userId = user.userId;
  const userRole = user.role;
  const navigate = useNavigate();
  const {
    messagenotification,
    notifications,
    markNotificationAsRead,
    clearMessageNotifications
  } = useSignalingStore();

  // Debug store subscription
  useEffect(() => {
    console.log('🏪 Store notifications changed:', {
      count: notifications.length,
      latest: notifications.length > 0 ? {
        id: notifications[notifications.length - 1].id,
        content: notifications[notifications.length - 1].content?.substring(0, 30) + '...',
        read: notifications[notifications.length - 1].read,
        type: notifications[notifications.length - 1].type,
        silent: notifications[notifications.length - 1].silent
      } : null
    });
  }, [notifications]);

  // Save closed notifications to localStorage
  useEffect(() => {
    localStorage.setItem('closedNotifications', JSON.stringify(closedNotifications));
  }, [closedNotifications]);

  // Setup user interaction tracking for audio autoplay
  useEffect(() => {
    // Function to handle user interaction
    const handleUserInteraction = () => {
      if (!userInteracted) {
        console.log('User has interacted with the page, enabling sounds');
        setUserInteracted(true);
        
        // Clear any existing timeout
        if (userInteractionTimeout.current) {
          clearTimeout(userInteractionTimeout.current);
        }
        
        // After 1 hour, reset the interaction flag (optional, for security)
        userInteractionTimeout.current = setTimeout(() => {
          setUserInteracted(false);
        }, 60 * 60 * 1000);  // 1 hour
      }
    };
    
    // Add event listeners for user interaction
    window.addEventListener('click', handleUserInteraction);
    window.addEventListener('keydown', handleUserInteraction);
    window.addEventListener('touchstart', handleUserInteraction);
    
    // Clean up event listeners
    return () => {
      window.removeEventListener('click', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
      window.removeEventListener('touchstart', handleUserInteraction);
      
      if (userInteractionTimeout.current) {
        clearTimeout(userInteractionTimeout.current);
      }
    };
  }, [userInteracted]);

  // Watch for new message notifications
  useEffect(() => {
    if (messagenotification.length > 0) {
      const latestMessage = messagenotification[messagenotification.length - 1];
      
      // Skip showing a snackbar for silent (already displayed) notifications
      if (latestMessage.silent) {
        console.log('Skipping already displayed message notification:', latestMessage.id);
        return;
      }
      
      // More detailed debug for message notifications
      console.log('💬 MESSAGE NOTIFICATION RAW DATA:');
      console.log('📨 Full message object:', latestMessage);
      console.log('💌 Message notification details:', {
        id: latestMessage.id,
        content: latestMessage.content,
        message: latestMessage.message,
        sender_id: latestMessage.sender_id,
        sender_name: latestMessage.sender_name,
        sender_full_name: latestMessage.sender_full_name,
        senderFullName: latestMessage.senderFullName,
        sender_image: latestMessage.sender_image ? 'Present' : 'Missing',
        senderImage: latestMessage.senderImage ? 'Present' : 'Missing',
        sender_company: latestMessage.sender_company,
        senderCompany: latestMessage.senderCompany,
        sender_company_logo: latestMessage.sender_company_logo ? 'Present' : 'Missing',
        senderCompanyLogo: latestMessage.senderCompanyLogo ? 'Present' : 'Missing',
        timestamp: latestMessage.timestamp,
        read: latestMessage.read,
        silent: latestMessage.silent,
        type: latestMessage.type,
        allFields: Object.keys(latestMessage)
      });
      
      if (userInteracted) {
        playMessageSound();
      } else {
        console.log('Sound not played: waiting for user interaction first');
      }
      
      // Extract image from message or notification data - check multiple possible fields
      const senderImage = latestMessage.sender_image || latestMessage.senderImage || latestMessage.image || null;

      // Only show snackbar if we have proper sender information
      // This prevents showing incomplete notifications with "New message" fallback
      if (latestMessage.sender_name && latestMessage.sender_name !== 'New message') {
        setMessageSnackbar({
          open: true,
          message: latestMessage.content || latestMessage.message || '',
          sender: latestMessage.sender_name,
          senderId: latestMessage.sender_id,
          senderImage: senderImage
        });
      } else {
        console.log('⏳ Skipping message snackbar - waiting for complete sender information:', {
          sender_name: latestMessage.sender_name,
          hasContent: !!(latestMessage.content || latestMessage.message),
          sender_id: latestMessage.sender_id
        });
      }
      
      // Debug the snackbar data being set (only if snackbar was actually set)
      if (latestMessage.sender_name && latestMessage.sender_name !== 'New message') {
        console.log('🎯 SETTING MESSAGE SNACKBAR:');
        console.log('📨 Message snackbar data:', {
          open: true,
          message: latestMessage.content || latestMessage.message || '',
          sender: latestMessage.sender_name,
          senderId: latestMessage.sender_id,
          senderImage: senderImage,
          senderImagePresent: senderImage ? 'Yes' : 'No',
          senderFullName: latestMessage.sender_full_name || latestMessage.senderFullName,
          senderCompany: latestMessage.sender_company || latestMessage.senderCompany,
          senderCompanyLogo: latestMessage.sender_company_logo || latestMessage.senderCompanyLogo
        });
      }
    }
  }, [messagenotification, userInteracted]);

  // Watch for new notifications
  useEffect(() => {
    console.log('🔔 NotificationContext: useEffect triggered', {
      notificationsLength: notifications.length,
      userInteracted,
      closedNotificationsLength: closedNotifications.length
    });

    if (notifications.length > 0) {
      // Get the LATEST notification (last in array, not first)
      const latestNotification = notifications[notifications.length - 1];

      console.log('🔔 NotificationContext: Processing latest notification:', {
        id: latestNotification.id,
        content: latestNotification.content?.substring(0, 30) + '...',
        sender: latestNotification.sender_name,
        read: latestNotification.read,
        silent: latestNotification.silent,
        totalNotifications: notifications.length,
        type: latestNotification.type
      });

      // Skip if notification was previously closed
      if (closedNotifications.includes(latestNotification.id)) {
        console.log('🚫 Skipping previously closed notification:', latestNotification.id);
        return;
      }

      // Skip showing a snackbar for silent (already displayed) notifications
      if (latestNotification.silent) {
        console.log('🔇 Skipping already displayed notification:', latestNotification.id);
        return;
      }

      console.log('🔔 NOTIFICATION RAW DATA:');
      console.log('📋 Full notification object:', latestNotification);
      console.log('🔔 Notification details:', {
        id: latestNotification.id,
        content: latestNotification.content,
        sender_id: latestNotification.sender_id,
        sender_name: latestNotification.sender_name,
        sender_full_name: latestNotification.sender_full_name,
        sender_image: latestNotification.sender_image ? 'Present' : 'Missing',
        sender_company: latestNotification.sender_company,
        sender_company_logo: latestNotification.sender_company_logo ? 'Present' : 'Missing',
        timestamp: latestNotification.timestamp || latestNotification.created_at,
        read: latestNotification.read,
        silent: latestNotification.silent,
        type: latestNotification.type,
        link: latestNotification.link,
        allFields: Object.keys(latestNotification)
      });

      if (latestNotification && !latestNotification.read) {
        console.log('✅ Notification is valid and unread, proceeding to show snackbar');

        if (userInteracted) {
          playNotificationSound();
        } else {
          console.log('🔇 Sound not played: waiting for user interaction first');
        }

        // Extract image from notification data - check multiple possible fields
        const senderImage = latestNotification.sender_image || latestNotification.senderImage || latestNotification.image || null;

        console.log('🎯 Setting notification snackbar to OPEN');
        const snackbarData = {
          open: true,
          message: latestNotification.content,
          link: latestNotification.link,
          id: latestNotification.id,
          sender_name: latestNotification.sender_name || 'System',
          sender_image: senderImage
        };

        console.log('📝 Snackbar data to be set:', snackbarData);
        setNotificationSnackbar(snackbarData);

        // Verify snackbar state after setting
        setTimeout(() => {
          console.log('🔍 Checking snackbar state after setting...');
        }, 50);

        // Debug the snackbar data being set
        console.log('🎯 SETTING NOTIFICATION SNACKBAR:');
        console.log('📋 Notification snackbar data:', {
          open: true,
          message: latestNotification.content,
          link: latestNotification.link,
          id: latestNotification.id,
          sender_name: latestNotification.sender_name || 'System',
          sender_image: senderImage,
          senderImagePresent: senderImage ? 'Yes' : 'No',
          sender_full_name: latestNotification.sender_full_name,
          sender_company: latestNotification.sender_company,
          sender_company_logo: latestNotification.sender_company_logo
        });
      } else {
        console.log('❌ Notification not shown - already read or invalid:', {
          hasNotification: !!latestNotification,
          isRead: latestNotification?.read,
          notificationData: latestNotification
        });
      }
    } else {
      console.log('📭 No notifications in array');
    }
  }, [notifications, userInteracted, closedNotifications]);

  // Play sound manually (helpful for testing)
  const testNotificationSound = () => {
    playNotificationSound();
    setUserInteracted(true);
  };

  const testMessageSound = () => {
    playMessageSound();
    setUserInteracted(true);
  };

  // Test function to manually show notification snackbar
  const testNotificationSnackbar = () => {
    console.log('🧪 Testing notification snackbar display...');
    setUserInteracted(true);
    setNotificationSnackbar({
      open: true,
      message: 'Test notification - snackbar is working!',
      link: null,
      id: Date.now(),
      sender_name: 'Test System',
      sender_image: null
    });
    playNotificationSound();
  };

  // Test function to manually show message snackbar
  const testMessageSnackbar = () => {
    console.log('🧪 Testing message snackbar display...');
    setUserInteracted(true);
    setMessageSnackbar({
      open: true,
      message: 'Test message - snackbar is working!',
      sender: 'Test User',
      senderId: 'test_user_123',
      senderImage: null
    });
    playMessageSound();
  };

  // Debug function to check current notification state
  const debugNotificationState = () => {
    console.log('🔍 Current notification state debug:', {
      notifications: notifications.length,
      notificationsList: notifications.map(n => ({
        id: n.id,
        content: n.content?.substring(0, 30) + '...',
        read: n.read,
        silent: n.silent,
        type: n.type
      })),
      messageNotifications: messagenotification.length,
      userInteracted,
      closedNotifications: closedNotifications.length,
      snackbarOpen: notificationSnackbar.open,
      snackbarMessage: notificationSnackbar.message
    });
    return {
      notifications,
      messagenotification,
      userInteracted,
      closedNotifications,
      notificationSnackbar
    };
  };

  // Test function to add notification directly to store and verify flow
  const testStoreNotification = () => {
    console.log('🧪 Testing store notification flow...');
    setUserInteracted(true);

    // Get the addNotification function from the store
    const { addNotification } = useSignalingStore.getState();

    const testNotif = {
      id: Date.now(),
      content: 'Test store notification - this should trigger snackbar!',
      timestamp: new Date().toISOString(),
      read: false,
      silent: false,
      type: 'notif',
      sender_id: userId,
      sender_name: 'Store Test',
      sender_image: null,
      link: null
    };

    console.log('🔔 Adding test notification to store:', testNotif);
    addNotification(testNotif);

    // Check store state after adding
    setTimeout(() => {
      const { notifications: updatedNotifications } = useSignalingStore.getState();
      console.log('🔍 Store state after adding test notification:', {
        total: updatedNotifications.length,
        latest: updatedNotifications[updatedNotifications.length - 1]
      });
    }, 100);
  };

  // Function to clear closed notifications for testing
  const clearClosedNotifications = () => {
    console.log('🧹 Clearing closed notifications list for testing...');
    setClosedNotifications([]);
    localStorage.removeItem('closedNotifications');
    console.log('✅ Closed notifications cleared');
  };

  const handleCloseMessageSnackbar = () => {
    setMessageSnackbar({
      ...messageSnackbar,
      open: false
    });
  };

  const handleCloseNotificationSnackbar = () => {
    if (notificationSnackbar.id) {
      // Add to closed notifications list
      setClosedNotifications(prev => [...prev, notificationSnackbar.id]);
      // Mark as read in the backend
      markNotificationAsRead(notificationSnackbar.id);
    }
    setNotificationSnackbar({
      ...notificationSnackbar,
      open: false
    });
  };

  useEffect(()=>{
    console.log('userRole',userRole);
  },[userRole])
  const handleMessageClick = () => {
    if (messageSnackbar.senderId) {
      clearMessageNotifications();
      navigate('/chat', { state: { selectedUser: messageSnackbar.senderId } });
      if(userRole === 'c'){
        navigate('/chat', { state: { selectedUser: messageSnackbar.senderId } });
      }else if (userRole === 'r'){
        navigate('/talent/chat', { state: { selectedUser: messageSnackbar.senderId } });
      }else if (userRole === 'a'){
        navigate('/admin/chat', { state: { selectedUser: messageSnackbar.senderId } });
      }
    } else {
      if(userRole === 'c'){
        navigate('/chat');
      }else if (userRole === 'r'){
        navigate('/talent/chat');
      }else if (userRole === 'a'){
        navigate('/admin/chat');
      }
    }
    handleCloseMessageSnackbar();
  };

  const handleNotificationClick = () => {
    if (notificationSnackbar.id) {
      markNotificationAsRead(notificationSnackbar.id);
    }
    if (notificationSnackbar.link) {
      navigate(notificationSnackbar.link);
    } else {
      navigate('/notifications');
    }
    handleCloseNotificationSnackbar();
  };

  // Expose test functions to window for debugging
  useEffect(() => {
    // @ts-ignore - Adding test functions to window for debugging
    window.testNotificationSnackbar = testNotificationSnackbar;
    window.testMessageSnackbar = testMessageSnackbar;
    window.debugNotificationState = debugNotificationState;
    window.testStoreNotification = testStoreNotification;
    console.log('🧪 Snackbar test functions exposed: window.testNotificationSnackbar(), window.testMessageSnackbar(), window.debugNotificationState(), window.testStoreNotification()');
  }, [testNotificationSnackbar, testMessageSnackbar, debugNotificationState, testStoreNotification]);

  // Add the context value with test sound functions
  const contextValue = {
    setMessageSnackbar,
    setNotificationSnackbar,
    testNotificationSound,
    testMessageSound,
    testNotificationSnackbar,
    testMessageSnackbar,
    userInteracted
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}

      {/* Message Snackbar - Glassmorphism Design */}
      <Snackbar
        open={messageSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseMessageSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionProps={{
          timeout: 400,
        }}
        sx={{
          "& .MuiSnackbarContent-root": {
            padding: 0,
            borderRadius: 3,
            boxShadow: "none",
            border: `1px solid ${colors.secondary}`,
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 0,
            ...glassEffect,
            width: { xs: "90vw", sm: 360, md: 380 },
            overflow: "hidden",
            cursor: "pointer",
            border: `1px solid ${colors.secondary}`,
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 14px 35px rgba(0, 0, 0, 0.07)",
border: `1px solid ${colors.primary}`,            },
            transition: "all 0.3s ease",
            animation: `${softPulse} 2.5s infinite, ${slideIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1)`,
          }}
          onClick={handleMessageClick}
        >
          {/* Header with gradient + glassmorphism */}
          {/* <Box
            sx={{
              display: "flex",
              alignItems: "center",
              background:
                "linear-gradient(135deg, rgba(79, 70, 229, 0.8) 0%, rgba(198, 199, 243, 0.8) 100%)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              px: 2.5,
              py: 1.5,
              position: "relative",
            }}
          >
            <MessageIcon
              sx={{ color: "white", fontSize: 18, mr: 1.5, opacity: 0.9 }}
            />
            <Typography
              variant="subtitle2"
              sx={{
                color: "white",
                fontWeight: 600,
                flexGrow: 1,
                letterSpacing: "0.01em",
                fontSize: "0.9rem",
                fontFamily: "Poppins",
                textTransform: "none",
              }}
            >
              New Message
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleCloseMessageSnackbar();
              }}
              sx={{
                p: 0.5,
                color: "white",
                opacity: 0.8,
                "&:hover": {
                  opacity: 1,
                  background: "rgba(255, 255, 255, 0.15)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box> */}

          {/* Message Snackbar Content */}
          <Box sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 2 }}>
            {/* User Avatar */}
            {messageSnackbar.senderImage ? (
              <CompanyLogo
                sx={{
                  width: 46,
                  height: 46,
                  border: "2px solid rgba(255, 255, 255, 0.7)",
                  boxShadow: "0 0 0 4px rgba(99, 102, 241, 0.04)",
                  backgroundImage: `url(${messageSnackbar.senderImage})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                {/* Fallback text in case image fails to load */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                    color: "#fff",
                    fontSize: "1.2rem",
                    fontWeight: 600,
                    fontFamily: "Poppins",
                    // Hide text when image is loaded successfully
                    opacity: 0,
                  }}
                >
                  {(messageSnackbar.senderFullName || messageSnackbar.sender)?.charAt(0).toUpperCase() || "M"}
                </Box>
              </CompanyLogo>
            ) : (
              <CompanyLogo
                sx={{
                  width: 46,
                  height: 46,
                  border: "2px solid rgba(255, 255, 255, 0.7)",
                  boxShadow: "0 0 0 4px rgba(99, 102, 241, 0.04)",
                }}
              >
                {(messageSnackbar.senderFullName || messageSnackbar.sender)?.charAt(0).toUpperCase() || "M"}
              </CompanyLogo>
            )}

            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: "#1E293B",
                  fontSize: "0.95rem",
                  mb: 0.5,
                  fontFamily: "Poppins",
                  textTransform: "capitalize",
                }}
              >
                {messageSnackbar.sender}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#475569",
                  maxWidth: "100%",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.4,
                  fontSize: "0.85rem",
                  fontFamily: "Poppins",
                }}
              >
                {messageSnackbar.message}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Snackbar>

      {/* Notification Snackbar - Glassmorphism Design */}
      <Snackbar
        open={notificationSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseNotificationSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionProps={{
          timeout: 400,
        }}
        sx={{
          zIndex: 9999, // Ensure it's on top
          "& .MuiSnackbarContent-root": {
            padding: 0,
            borderRadius: 3,
            boxShadow: "none",
            border: `1px solid ${colors.secondary}`,
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 0,
            ...glassEffect,
            width: { xs: "90vw", sm: 360, md: 380 },
            overflow: "hidden",
            cursor: "pointer",
            border: `1px solid ${colors.secondary}`,
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 14px 35px rgba(0, 0, 0, 0.07)",
              border: `1px solid ${colors.primary}`,
            },
            transition: "all 0.3s ease",
            animation: `${softPulse} 2.5s infinite, ${slideIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1)`,
          }}
          onClick={handleNotificationClick}
        >
          {/* Notification Snackbar Content */}
          <Box sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 2 }}>
            {/* User Avatar */}
            {notificationSnackbar.sender_image ? (
              <CompanyLogo
                sx={{
                  width: 46,
                  height: 46,
                  border: "2px solid rgba(255, 255, 255, 0.7)",
                  boxShadow: "0 0 0 4px rgba(99, 102, 241, 0.04)",
                  backgroundImage: `url(${notificationSnackbar.sender_image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                {/* Fallback text in case image fails to load */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                    color: "#fff",
                    fontSize: "1.2rem",
                    fontWeight: 600,
                    fontFamily: "Poppins",
                    // Hide text when image is loaded successfully
                    opacity: 0,
                  }}
                >
                  {notificationSnackbar.sender_name?.charAt(0).toUpperCase() || "N"}
                </Box>
              </CompanyLogo>
            ) : (
              <CompanyLogo
                sx={{
                  width: 46,
                  height: 46,
                  border: "2px solid rgba(255, 255, 255, 0.7)",
                  boxShadow: "0 0 0 4px rgba(99, 102, 241, 0.04)",
                }}
              >
                {notificationSnackbar.sender_name?.charAt(0).toUpperCase() || "N"}
              </CompanyLogo>
            )}

            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: "#1E293B",
                  fontSize: "0.95rem",
                  mb: 0.5,
                  fontFamily: "Poppins",
                  textTransform: "capitalize",
                }}
              >
                {notificationSnackbar.sender_name || "Notification"}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#475569",
                  maxWidth: "100%",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.4,
                  fontSize: "0.85rem",
                  fontFamily: "Poppins",
                }}
              >
                {notificationSnackbar.message}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;